import { motion } from "framer-motion";
import { Wallet, TrendingUp, FileSignature, CreditCard, ArrowRight } from "lucide-react";

const products = [
  {
    icon: Wallet,
    title: "Merchant Cash Advance",
    badge: "Most Popular",
    range: "$10K – $2M",
    speed: "24-hour funding",
    body: "Get a lump sum of capital advanced against your future card sales. Daily or weekly remittance. Perfect for businesses with strong revenue but limited credit.",
    bullets: ["Approval in hours", "Bad credit OK", "No collateral required"],
    span: "lg:col-span-7 lg:row-span-2",
    featured: true,
  },
  {
    icon: TrendingUp,
    title: "Revenue-Based Financing",
    range: "$25K – $5M",
    speed: "48-hour funding",
    body: "Repay as a percentage of monthly revenue — payments flex with your business cycle.",
    bullets: ["Cashflow-friendly", "No fixed term"],
    span: "lg:col-span-5",
    featured: false,
  },
  {
    icon: FileSignature,
    title: "Term Loans",
    range: "$50K – $5M",
    speed: "3–7 day funding",
    body: "Fixed amount, fixed rate, fixed term. The lowest cost of capital we offer.",
    bullets: ["12 – 60 month terms", "Best rates"],
    span: "lg:col-span-3",
    featured: false,
  },
  {
    icon: CreditCard,
    title: "Business Line of Credit",
    range: "$10K – $250K",
    speed: "Same-day draws",
    body: "Revolving capital you tap when you need it. Pay interest only on what you use.",
    bullets: ["Draw anytime", "Reusable capital"],
    span: "lg:col-span-2",
    featured: false,
  },
];

export default function FundingProducts() {
  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-32 bg-[#070F1B] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold mb-5">Funding Products</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tighter">
              The right capital, matched to your business.
            </h2>
          </div>
          <a href="#apply" className="text-gold hover:text-gold-400 text-sm font-semibold inline-flex items-center gap-2 group">
            Compare all products <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 auto-rows-fr">
          {products.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`product-${i}`}
              className={`trace-border ${p.span} p-8 md:p-10 flex flex-col ${
                p.featured
                  ? "bg-gradient-to-br from-[#0A1326] via-[#0A1326] to-[#152544]"
                  : "bg-[#0A1326]"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className={`w-12 h-12 flex items-center justify-center ${p.featured ? "bg-gold text-navy-950" : "border border-gold/40 text-gold"}`}>
                  <p.icon className="w-6 h-6" />
                </div>
                {p.badge && (
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-navy-950 bg-gold px-3 py-1">
                    {p.badge}
                  </span>
                )}
              </div>
              <h3 className="mt-6 font-heading text-2xl md:text-3xl text-white">{p.title}</h3>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <span className="font-heading text-xl text-gold">{p.range}</span>
                <span className="text-xs text-white/50 uppercase tracking-wider">· {p.speed}</span>
              </div>
              <p className="mt-4 text-white/60 leading-relaxed">{p.body}</p>
              <ul className="mt-5 space-y-2 mb-6">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-white/70">
                    <span className="w-1 h-1 bg-gold rounded-full" /> {b}
                  </li>
                ))}
              </ul>
              <a
                href="#apply"
                className="mt-auto inline-flex items-center gap-2 text-gold hover:text-gold-400 text-sm font-semibold group"
              >
                Get this funding <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
